'use client';
import { useMountEffect } from '@react-hookz/web';
import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import { useState } from 'react';
import { useScreen } from '../../../../../../hooks/useScreen';
import type { DeviceDetailsType } from '../../types';
import { isIpadMasqueradingAsDesktop } from '../../utils/isIpadMasqueradingAsDesktop';

// The device is a guess: matrix derives it from the user agent (apps/matrix/src/proxy.ts), which
// cannot see an iPad (see isIpadMasqueradingAsDesktop) and cannot see a desktop browser at a
// phone-sized viewport — a narrow window, or the app embedded in a small iframe on someone else's
// site. Both corrections wait for mount so the first render still matches the markup the server
// sent, and the returned `key` then rebuilds the subtree against the corrected device instead of
// leaving block state computed for the wrong one silently stale.
//
// Two limits on how far the correction reaches, both load-bearing:
//   - Mount only. Re-keying on every later breakpoint change would throw away the whole app store
//     — form input, chat history, scroll position — and a phone rotating to landscape crosses into
//     the tablet breakpoint, so that would fire on an ordinary rotation.
//   - It only narrows to mobile. A touch laptop at 1366px also reads as a tablet by breakpoint,
//     and it should keep the desktop layout its user agent asked for.
export function useResolvedRunnerDeviceDetails(
  serverDeviceDetails: DeviceDetailsType | undefined,
): { deviceDetails: DeviceDetailsType; key: string } {
  const { screen } = useScreen();
  const [correctedDevice, setCorrectedDevice] = useState<DeviceVariantType>();

  useMountEffect(() => {
    const serverDevice = serverDeviceDetails?.device;

    if (screen === DeviceVariantType.MOBILE && serverDevice !== DeviceVariantType.MOBILE) {
      setCorrectedDevice(DeviceVariantType.MOBILE);
      return;
    }

    if (serverDevice === DeviceVariantType.DESKTOP && isIpadMasqueradingAsDesktop()) {
      setCorrectedDevice(DeviceVariantType.TABLET);
    }
  });

  const device = correctedDevice ?? serverDeviceDetails?.device ?? screen;

  return { deviceDetails: { ...serverDeviceDetails, device }, key: device };
}
